/* eslint-disable unicorn/prefer-module */
/* eslint-disable no-console */
import { useState, useEffect, useMemo, useRef } from "react";
import { Dropdown } from "react-bootstrap";
import { FaPlay, FaPause } from "react-icons/fa";
import { FaBackwardStep, FaForwardStep } from "react-icons/fa6";
import { IoIosCloudDownload } from "react-icons/io";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useAuthContext } from "../../context/auth";
import { getEdificiosFull, getClientes, getCategorias } from "../../utils";
import { photosData } from "./photosData";
import "./stylesGaleria.scss";

// Función para barajar un array (Fisher-Yates)
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Función para capitalizar categorías
const capitalizeFirst = (str) => {
  if (!str) return str;
  const parts = str.split(" & ");
  return parts
    .map((part) =>
      part
        .trim()
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(" ")
    )
    .join(" & ");
};

const Galeria = () => {
  const [photos, setPhotos] = useState([]);
  const [filteredPhotos, setFilteredPhotos] = useState([]);
  const [filters, setFilters] = useState({
    category: "",
    client: "",
    building: "",
    country: "",
    screenType: "",
  });
  const [edificios, setEdificios] = useState({});
  const [categorias, setCategorias] = useState({});
  const [clientes, setClientes] = useState({});
  const { pais } = useAuthContext();
  const [isPlaying, setIsPlaying] = useState(true);
  const sliderRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0); // Nuevo estado para el slide actual

  // Obtener datos de todos los edificios
  const fetchEdificios = async () => {
    try {
      const data = await getEdificiosFull(); // Usar getEdificiosFull para todos los edificios
      if (data?.length > 0) {
        const edificiosMap = data.reduce((acc, edificio) => {
          acc[edificio.id] = {
            edificio: edificio.edificio || "Sin Nombre",
            pais: edificio.pais || "Sin País", // Asegurar que pais se incluya
          };
          return acc;
        }, {});
        setEdificios(edificiosMap);
      } else {
        setEdificios({});
      }
    } catch (error) {
      console.error("Error en fetchEdificios:", error);
    }
  };

  // Obtener datos de todas las categorías
  const fetchCategorias = async () => {
    try {
      const data = await getCategorias();
      if (data?.length > 0) {
        const categoriasMap = data.reduce((acc, categoria) => {
          acc[categoria.id] = categoria.nombre;
          return acc;
        }, {});
        setCategorias(categoriasMap);
      }
    } catch (error) {
      console.error("Error en fetchCategorias:", error);
    }
  };

  // Obtener datos de clientes
  const fetchClientes = async () => {
    try {
      const data = await getClientes({});
      if (data?.length > 0) {
        const clientesMap = data.reduce((acc, cliente) => {
          acc[cliente.id] = cliente;
          return acc;
        }, {});
        setClientes(clientesMap);
      }
    } catch (error) {
      console.error("Error en fetchClientes:", error);
    }
  };

  useEffect(() => {
    fetchEdificios();
    fetchCategorias();
    fetchClientes();
    const shuffledPhotos = shuffleArray(photosData);
    setPhotos(shuffledPhotos);
    setFilteredPhotos(shuffledPhotos);
  }, []);

  // Mapa para convertir screenType legible a código
  const screenTypeMap = {
    Ascensores: "asc",
    Circulación: "cir",
    Espera: "esp",
    Cowork: "cow",
    "Directorios Digitales": "dir",
  };

  // Mapa de categorías unificadas
  const categoryMapping = {
    2: "Aerolíneas",
    4: "Automotriz",
    5: "Automotriz",
    6: "Automotriz",
    35: "Tecnología",
    36: "Electrohogar",
    44: "Tecnología",
    50: "Tecnología",
    14: "Educación",
    15: "Educación",
  };

  // Helper para aplicar filtros
  const applyFiltersHelper = (currentFilters, photosList) => {
    let filtered = [...photosList];
    if (currentFilters.category) {
      filtered = filtered.filter((photo) => {
        if (photo.idcliente && clientes[photo.idcliente]?.categoria) {
          const categoriaId = clientes[photo.idcliente].categoria;
          const categoriaNombre = capitalizeFirst(categoryMapping[categoriaId] || categorias[categoriaId]);
          return categoriaNombre === currentFilters.category;
        }
        return "Sin Categoría" === currentFilters.category;
      });
    }
    if (currentFilters.client) {
      filtered = filtered.filter(
        (photo) => (photo.idcliente ? clientes[photo.idcliente]?.nombre : "Sin Cliente") === currentFilters.client
      );
    }
    if (currentFilters.building) {
      filtered = filtered.filter((photo) => {
        const buildingName = edificios[photo.idedificio]?.edificio || "Sin Edificio";
        return buildingName === currentFilters.building;
      });
    }
    if (currentFilters.country) {
      filtered = filtered.filter(
        (photo) => (photo.idcliente ? clientes[photo.idcliente]?.pais : "Sin País") === currentFilters.country
      );
    }
    if (currentFilters.screenType) {
      filtered = filtered.filter((photo) => photo.screenType === screenTypeMap[currentFilters.screenType]);
    }
    return filtered;
  };

  // Opciones dinámicas para dropdowns
  const categoryOptions = useMemo(() => {
    const tempFilters = { ...filters, category: "" };
    const filtered = applyFiltersHelper(tempFilters, photos);
    const cats = new Set(
      filtered
        .map((p) => {
          if (p.idcliente && clientes[p.idcliente]?.categoria) {
            const categoriaId = clientes[p.idcliente].categoria;
            const rawCategory = categoryMapping[categoriaId] || categorias[categoriaId];
            return capitalizeFirst(rawCategory);
          }
          return null;
        })
        .filter(Boolean)
    );
    return [...cats].sort();
  }, [filters, clientes, categorias, photos]);

  const clientOptions = useMemo(() => {
    const tempFilters = { ...filters, client: "" };
    const filtered = applyFiltersHelper(tempFilters, photos);
    const clientsSet = new Set(
      filtered
        .map((p) => (p.idcliente && clientes[p.idcliente]?.nombre ? clientes[p.idcliente].nombre : null))
        .filter(Boolean)
    );
    return [...clientsSet].sort();
  }, [filters, clientes, photos]);

  const buildingOptions = useMemo(() => {
    const tempFilters = { ...filters, building: "" };
    const filtered = applyFiltersHelper(tempFilters, photos);
    const buildingsSet = new Set(filtered.map((p) => edificios[p.idedificio]?.edificio).filter(Boolean));
    return [...buildingsSet].sort();
  }, [filters, edificios, photos]);

  const countryOptions = useMemo(() => {
    const tempFilters = { ...filters, country: "" };
    const filtered = applyFiltersHelper(tempFilters, photos);
    const countriesSet = new Set(
      filtered
        .map((p) => (p.idcliente && clientes[p.idcliente]?.pais ? clientes[p.idcliente].pais : null))
        .filter(Boolean)
    );
    return [...countriesSet].sort();
  }, [filters, clientes, photos]);

  const screenTypeOptions = useMemo(() => {
    const tempFilters = { ...filters, screenType: "" };
    const filtered = applyFiltersHelper(tempFilters, photos);
    const typesSet = new Set(
      filtered.map((p) => Object.entries(screenTypeMap).find(([key, val]) => val === p.screenType)?.[0]).filter(Boolean)
    );
    return [...typesSet].sort();
  }, [filters, photos]);

  // Aplicar filtros
  const applyFilters = (currentFilters) => {
    const filtered = applyFiltersHelper(currentFilters, photos);
    setFilteredPhotos(filtered);
  };

  // Manejar cambio en filtros
  const handleFilterChange = (name, value) => {
    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);
    applyFilters(newFilters);
  };
  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4500,
    pauseOnHover: false,
    arrows: false,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 1 } },
      { breakpoint: 768, settings: { slidesToShow: 1 } },
    ],
  };

  const logo = useMemo(
    () =>
      ({
        Chile: require("../../img/wecast-logo.png"),
        Perú: require("../../img/visiona-logo.png"),
        Uruguay: require("../../img/screenmedia-logo.png"),
        Region: require("../../img/adigital-logo.png"),
      })[pais] || require("../../img/visiona-logo.png"),
    [pais]
  );

  // Función para retroceder imagen
  const goToPrev = () => {
    if (sliderRef.current) {
      sliderRef.current.slickPrev();
    }
  };

  // Función para avanzar imagen
  const goToNext = () => {
    if (sliderRef.current) {
      sliderRef.current.slickNext();
    }
  };

  // Función para alternar pausa/play
  const togglePlayPause = () => {
    if (sliderRef.current) {
      if (isPlaying) {
        sliderRef.current.slickPause();
      } else {
        sliderRef.current.slickPlay();
      }
      setIsPlaying(!isPlaying);
    }
  };

  // Función para descargar la imagen actual con el logo superpuesto
  const downloadImage = async () => {
    const currentSlide = sliderRef.current?.innerSlider?.state.currentSlide;
    if (filteredPhotos[currentSlide]) {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = filteredPhotos[currentSlide].src;
      await new Promise((resolve) => (img.onload = resolve));
      const logoImg = new Image();
      logoImg.crossOrigin = "anonymous";
      logoImg.src = logo;
      await new Promise((resolve) => (logoImg.onload = resolve));
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const logoHeight = img.height * 0.08;
      const logoWidth = (logoImg.width / logoImg.height) * logoHeight;
      const x = (img.width - logoWidth) / 2;
      const y = img.height - logoHeight - 10;
      ctx.drawImage(logoImg, x, y, logoWidth, logoHeight);
      const countryPrefix = pais === "Chile" ? "Wecast" : pais === "Perú" ? "Visiona" : "Adigital";
      const photo = filteredPhotos[currentSlide];
      const clientId = photo.idcliente;
      const category =
        clientId && clientes[clientId]?.categoria
          ? capitalizeFirst(categoryMapping[clientes[clientId].categoria] || categorias[clientes[clientId].categoria])
          : "";
      const client = clientId && clientes[clientId]?.nombre ? clientes[clientId].empresa : "";
      const building = photo.idedificio && edificios[photo.idedificio] ? edificios[photo.idedificio] : "";
      const fileNameParts = [countryPrefix];
      if (category) fileNameParts.push(category);
      if (client) fileNameParts.push(client);
      if (building) fileNameParts.push(building);
      const fileName = `${fileNameParts.join(". ")}.jpg`;
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/jpeg");
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Obtener datos de la foto actual
  const getCurrentPhotoData = () => {
    if (filteredPhotos[currentSlide]) {
      const photo = filteredPhotos[currentSlide];
      const clientId = photo.idcliente;
      let client, category; // Sin valor por defecto, se definirá solo si no es 'xx'
      const srcParts = photo.src.split("-");
      const edificioIdFromSrc = srcParts[1]; // Segundo segmento es el edificioID (e.g., '135')
      const buildingData = edificioIdFromSrc && edificios[edificioIdFromSrc] ? edificios[edificioIdFromSrc] : null;
      const building = buildingData ? buildingData.edificio : "Sin Edificio";
      let country = "Sin País";
      const screenType =
        Object.entries(screenTypeMap).find(([key, val]) => val === photo.screenType)?.[0] || "Sin Circuito";

      // Obtener país desde edificios para todas las fotos
      if (buildingData && buildingData.pais) {
        country = buildingData.pais;
      }

      // Si no empieza con 'xx' y tiene clientId, usar datos del cliente
      if (srcParts[0] !== "xx" && clientId) {
        client = clientes[clientId]?.nombre || "Sin Cliente";
        category =
          clientId && clientes[clientId]?.categoria
            ? capitalizeFirst(categoryMapping[clientes[clientId].categoria] || categorias[clientes[clientId].categoria])
            : "Sin Categoría";
        if (clientes[clientId]?.pais) {
          country = clientes[clientId].pais; // Priorizar país del cliente si existe
        }
      }

      return { client, category, screenType, building, country };
    }
    return {
      client: undefined,
      category: undefined,
      screenType: "Sin Circuito",
      building: "Sin Edificio",
      country: "Sin País",
    };
  };

  return (
    <div className="gallery-container">
      <div className="left-section">
        <div className="filters-group">
          <Dropdown onSelect={(value) => handleFilterChange("category", value)}>
            <Dropdown.Toggle
              id="dropdown-category"
              className={`text-start ${filters.category !== "" ? "selected" : ""}`}
              data-selected={filters.category !== "" ? "true" : undefined}
            >
              {filters.category ? capitalizeFirst(filters.category) : "Categorías"}
            </Dropdown.Toggle>
            <Dropdown.Menu className="custom-dropdown-menu">
              <Dropdown.Item eventKey="">{filters.category ? "Limpiar" : "Seleccione"}</Dropdown.Item>
              {categoryOptions.map((catName) => (
                <Dropdown.Item key={catName} eventKey={catName}>
                  {catName}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <Dropdown onSelect={(value) => handleFilterChange("client", value)}>
            <Dropdown.Toggle
              id="dropdown-client"
              className={`text-start ${filters.client !== "" ? "selected" : ""}`}
              data-selected={filters.client !== "" ? "true" : undefined}
            >
              {filters.client || "Clientes"}
            </Dropdown.Toggle>
            <Dropdown.Menu className="custom-dropdown-menu">
              <Dropdown.Item eventKey="">{filters.client ? "Limpiar" : "Seleccione"}</Dropdown.Item>
              {clientOptions.map((name) => (
                <Dropdown.Item key={name} eventKey={name}>
                  {name}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <Dropdown onSelect={(value) => handleFilterChange("building", value)}>
            <Dropdown.Toggle
              id="dropdown-building"
              className={`text-start ${filters.building !== "" ? "selected" : ""}`}
              data-selected={filters.building !== "" ? "true" : undefined}
            >
              {filters.building || "Edificios"}
            </Dropdown.Toggle>
            <Dropdown.Menu className="custom-dropdown-menu">
              <Dropdown.Item eventKey="">{filters.building ? "Limpiar" : "Seleccione"}</Dropdown.Item>
              {buildingOptions.map((name) => (
                <Dropdown.Item key={name} eventKey={name}>
                  {name}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <Dropdown onSelect={(value) => handleFilterChange("country", value)}>
            <Dropdown.Toggle
              id="dropdown-country"
              className={`text-start ${filters.country !== "" ? "selected" : ""}`}
              data-selected={filters.country !== "" ? "true" : undefined}
            >
              {filters.country || "Países"}
            </Dropdown.Toggle>
            <Dropdown.Menu className="custom-dropdown-menu">
              <Dropdown.Item eventKey="">{filters.country ? "Limpiar" : "Seleccione"}</Dropdown.Item>
              {countryOptions.map((name) => (
                <Dropdown.Item key={name} eventKey={name}>
                  {name}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
          <Dropdown onSelect={(value) => handleFilterChange("screenType", value)}>
            <Dropdown.Toggle
              id="dropdown-screenType"
              className={`text-start ${filters.screenType !== "" ? "selected" : ""}`}
              data-selected={filters.screenType !== "" ? "true" : undefined}
            >
              {filters.screenType || "Circuito"}
            </Dropdown.Toggle>
            <Dropdown.Menu className="custom-dropdown-menu">
              <Dropdown.Item eventKey="">{filters.screenType ? "Limpiar" : "Seleccione"}</Dropdown.Item>
              {screenTypeOptions.map((type) => (
                <Dropdown.Item key={type} eventKey={type}>
                  {type}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
        <div className="photo-info">
          {Object.entries(getCurrentPhotoData()).map(([label, value]) => (
            <p key={label}>{value}</p>
          ))}
        </div>
        <div className="logo-container-galeria">
          <img src={logo} alt={`${pais} Logo`} className="logo" />
        </div>
      </div>
      <div className="right-section">
        <div className="carousel-wrapper">
          <Slider ref={sliderRef} {...sliderSettings} afterChange={(index) => setCurrentSlide(index)}>
            {filteredPhotos.length > 0 ? (
              filteredPhotos.map((photo, index) => (
                <div key={index} className="slide-item">
                  <img
                    src={photo.src}
                    alt={
                      photo.idcliente && clientes[photo.idcliente]
                        ? `Foto de ${clientes[photo.idcliente].nombre} (${photo.screenType})`
                        : photo.alt ||
                          `Sin cliente asociado - ${edificios[photo.idedificio] || "Edificio desconocido"} - ${photo.screenType}`
                    }
                    className="carousel-image"
                    loading="lazy"
                    onError={(e) => console.error("Image load error for:", photo.src, e)}
                  />
                </div>
              ))
            ) : (
              <div className="slide-item">
                <p>No hay fotos que coincidan con los filtros</p>
              </div>
            )}
          </Slider>
          <div className="carousel-controls">
            <button onClick={goToPrev} className="control-btn">
              <FaBackwardStep />
            </button>
            <button onClick={togglePlayPause} className="control-btn">
              {isPlaying ? <FaPause /> : <FaPlay />}
            </button>
            <button onClick={downloadImage} className="control-btn">
              <IoIosCloudDownload />
            </button>
            <button onClick={goToNext} className="control-btn">
              <FaForwardStep />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Galeria;
